import {
  Users, UserCheck, UserX, Clock, TrendingUp, TrendingDown,
  CalendarCheck, AlertCircle, CheckCircle, XCircle, Briefcase,
  FolderOpen, Activity, BarChart3, Coffee, ClipboardList,
} from 'lucide-react';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { cn } from '@/shared/lib/utils';
import { useEmployees } from '../hooks/useEmployees';
import { useTimeEntries, useTimesheet } from '../hooks/useTimeEntry';
import { useAdjustmentRequests } from '../hooks/useAdjustmentRequests';
import { useProjects, useTimeAllocations } from '../hooks/useProjects';
import { formatHours, formatDateForAPI, toDateString, type Timesheet, type TimeEntry } from '../types';

// ==========================================
// Helpers de data
// ==========================================
function getToday() {
  return formatDateForAPI(new Date());
}

function getMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: formatDateForAPI(start), endDate: formatDateForAPI(end) };
}

// ==========================================
// MetricCard
// ==========================================
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  variant?: 'default' | 'positive' | 'negative' | 'neutral' | 'warning';
  loading?: boolean;
}

function MetricCard({ title, value, subtitle, icon: Icon, variant = 'default', loading }: MetricCardProps) {
  const iconColors = {
    default: 'text-primary bg-primary/10',
    positive: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-950/40',
    negative: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-950/40',
    neutral: 'text-muted-foreground bg-muted',
    warning: 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-950/40',
  };
  const valueColors = {
    default: 'text-foreground',
    positive: 'text-green-600 dark:text-green-400',
    negative: 'text-red-600 dark:text-red-400',
    neutral: 'text-muted-foreground',
    warning: 'text-amber-600 dark:text-amber-400',
  };

  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        {loading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-7 w-16 bg-muted rounded" />
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1 truncate">{title}</p>
              <p className={cn('text-2xl font-bold font-mono', valueColors[variant])}>{value}</p>
              {subtitle && <p className="text-xs text-muted-foreground mt-1 leading-tight">{subtitle}</p>}
            </div>
            <div className={cn('p-2.5 rounded-xl shrink-0', iconColors[variant])}>
              <Icon className="h-5 w-5" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// SectionTitle
// ==========================================
function SectionTitle({ icon: Icon, title, description }: {
  icon: React.ElementType;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="p-2 rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

// ==========================================
// Barra de progresso
// ==========================================
function MiniProgressBar({ value, max, colorClass }: { value: number; max: number; colorClass?: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-1.5">
      <div
        className={cn('h-full rounded-full transition-all duration-500', colorClass ?? 'bg-primary')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ==========================================
// Card de ranking colaborador
// ==========================================
function RankCard({
  label,
  name,
  value,
  variant,
}: { label: string; name: string; value: string; variant: 'positive' | 'negative' }) {
  return (
    <div className={cn(
      'flex items-center justify-between rounded-lg px-3 py-2.5 border',
      variant === 'positive'
        ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
        : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
    )}>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium truncate max-w-[140px]">{name}</p>
      </div>
      <span className={cn(
        'font-mono text-sm font-semibold',
        variant === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
      )}>
        {value}
      </span>
    </div>
  );
}

// ==========================================
// MetricsPeoplePage
// ==========================================
export default function MetricsPeoplePage() {
  const today = getToday();
  const { startDate, endDate } = getMonthRange();

  // ── Dados base ──────────────────────────────────────────
  const { data: employees, isLoading: loadingEmployees } = useEmployees();
  const { data: todayEntries, isLoading: loadingToday } = useTimeEntries({ startDate: today, endDate: today });
  const { data: monthTimesheet, isLoading: loadingTimesheet } = useTimesheet({ startDate, endDate });
  const { data: allAdjustments, isLoading: loadingAdj } = useAdjustmentRequests({ startDate, endDate });
  const { data: pendingAdjustments } = useAdjustmentRequests({ status: 'PENDING' });
  const { data: projects, isLoading: loadingProjects } = useProjects(true);
  const { data: todayAllocations, isLoading: loadingTodayAlloc } = useTimeAllocations({ startDate: today, endDate: today });
  const { data: monthAllocations, isLoading: loadingMonthAlloc } = useTimeAllocations({ startDate, endDate });

  const isLoading = loadingEmployees || loadingToday || loadingTimesheet;

  // ── Seção 1: Colaboradores ───────────────────────────────
  const totalEmployees = employees?.length ?? 0;

  // Colaboradores que bateram ponto hoje (tem clockIn)
  const onlineToday = (todayEntries ?? []).filter((e: TimeEntry) => e.clockIn).length;

  // Taxa de presença
  const presenceRate = totalEmployees > 0 ? Math.round((onlineToday / totalEmployees) * 100) : 0;

  // Ponto pendente: clockIn sem clockOut (expediente aberto)
  const pendingClockOut = (todayEntries ?? []).filter((e: TimeEntry) => e.clockIn && !e.clockOut).length;

  // Em intervalo agora: breakStart sem breakEnd
  const onBreakNow = (todayEntries ?? []).filter((e: TimeEntry) => e.breakStart && !e.breakEnd).length;

  // ── Seção 2: Banco de horas ──────────────────────────────
  // Agrupa timesheet do mês por employeeId para pegar último acumulado
  const accByEmployee: Record<string, { name: string; balance: number }> = {};

  (monthTimesheet ?? []).forEach((row: Timesheet) => {
    // O acumulado mais recente de cada colaborador
    const name = row.employeeId; // fallback — substituído abaixo
    if (!accByEmployee[row.employeeId] || new Date(row.date) > new Date(accByEmployee[row.employeeId] as unknown as string)) {
      accByEmployee[row.employeeId] = { name, balance: row.accumulatedBalance };
    }
  });

  // Enriquece nomes via employees
  (employees ?? []).forEach((emp) => {
    if (accByEmployee[emp.id]) {
      accByEmployee[emp.id].name = emp.user?.name ?? emp.id;
    }
  });

  const employeeBalances = Object.values(accByEmployee);
  const totalBalance = employeeBalances.reduce((s, e) => s + e.balance, 0);
  const avgBalance = employeeBalances.length > 0 ? totalBalance / employeeBalances.length : 0;
  const positiveCount = employeeBalances.filter((e) => e.balance > 0).length;
  const negativeCount = employeeBalances.filter((e) => e.balance < 0).length;

  const maxPositive = employeeBalances.reduce(
    (best, e) => (e.balance > best.balance ? e : best),
    { name: '—', balance: 0 }
  );
  const maxNegative = employeeBalances.reduce(
    (worst, e) => (e.balance < worst.balance ? e : worst),
    { name: '—', balance: 0 }
  );

  // ── Seção 3: Hoje ─────────────────────────────────────────
  const totalWorkedToday = (monthTimesheet ?? [])
    .filter((r: Timesheet) => toDateString(r.date) === today)
    .reduce((s: number, r: Timesheet) => s + r.hoursWorked, 0);

  const totalExpectedToday = (employees ?? []).reduce(
    (s, e) => s + (e.dailyWorkHours ?? 8),
    0
  );

  const totalLaunchedToday = (todayAllocations ?? []).reduce((s, a) => s + a.hours, 0);

  // ── Seção 4: Ajustes ─────────────────────────────────────
  const pendingCount = pendingAdjustments?.length ?? 0;
  const overdueCount = (pendingAdjustments ?? []).filter((r) => r.sla?.isOverdue).length;
  const approvedCount = (allAdjustments ?? []).filter((r) => r.status === 'APPROVED').length;
  const rejectedCount = (allAdjustments ?? []).filter((r) => r.status === 'REJECTED').length;
  const totalReviewed = approvedCount + rejectedCount;
  const approvalRate = totalReviewed > 0 ? Math.round((approvedCount / totalReviewed) * 100) : 0;

  // ── Seção 5: Projetos & Alocação ─────────────────────────
  const activeProjects = (projects ?? []).filter((p) => p.isActive);
  const totalActivities = activeProjects.reduce(
    (s, p) => s + (p.activities?.filter((a) => a.isActive).length ?? 0),
    0
  );

  const totalMonthAllocated = (monthAllocations ?? []).reduce((s, a) => s + a.hours, 0);
  const totalMonthWorked = (monthTimesheet ?? []).reduce((s: number, r: Timesheet) => s + r.hoursWorked, 0);
  const allocationRate = totalMonthWorked > 0
    ? Math.round((totalMonthAllocated / totalMonthWorked) * 100)
    : 0;

  // ── Labels mês ───────────────────────────────────────────
  const monthLabel = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Métricas da Equipe</h1>
        <p className="text-muted-foreground mt-1 capitalize">{monthLabel} · visão administrativa</p>
      </div>

      {/* ═══════════════════════════════════════════════
          SEÇÃO 1: COLABORADORES
      ═══════════════════════════════════════════════ */}
      <div className="space-y-3">
        <SectionTitle icon={Users} title="Colaboradores" description="Status geral da equipe" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard
            title="Total de Colaboradores"
            value={totalEmployees}
            subtitle="Cadastros ativos"
            icon={Users}
            variant="default"
            loading={loadingEmployees}
          />
          <MetricCard
            title="Online Hoje"
            value={onlineToday}
            subtitle={`de ${totalEmployees} colaboradores`}
            icon={UserCheck}
            variant={onlineToday > 0 ? 'positive' : 'neutral'}
            loading={isLoading}
          />
          <MetricCard
            title="Taxa de Presença"
            value={`${presenceRate}%`}
            subtitle={`${onlineToday} bateram ponto hoje`}
            icon={BarChart3}
            variant={presenceRate >= 80 ? 'positive' : presenceRate >= 50 ? 'warning' : 'negative'}
            loading={isLoading}
          />
          <MetricCard
            title="Ponto Pendente"
            value={pendingClockOut}
            subtitle={pendingClockOut === 0 ? 'Nenhum expediente aberto' : `${pendingClockOut} sem registrar saída`}
            icon={pendingClockOut === 0 ? UserCheck : UserX}
            variant={pendingClockOut === 0 ? 'positive' : 'warning'}
            loading={isLoading}
          />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════
          SEÇÃO 3: HOJE  (movida para antes do banco)
      ═══════════════════════════════════════════════ */}
      <div className="space-y-3">
        <SectionTitle icon={Clock} title="Tempo Real — Hoje" description="Dados do dia atual para toda a equipe" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard
            title="Horas Trabalhadas"
            value={formatHours(totalWorkedToday)}
            subtitle="Total da equipe hoje"
            icon={Clock}
            variant="default"
            loading={isLoading}
          />
          <MetricCard
            title="Horas Esperadas"
            value={formatHours(totalExpectedToday)}
            subtitle="Jornada total contratual"
            icon={CalendarCheck}
            variant="neutral"
            loading={loadingEmployees}
          />
          <MetricCard
            title="Em Intervalo Agora"
            value={onBreakNow}
            subtitle={onBreakNow === 0 ? 'Nenhum em pausa' : `colaborador${onBreakNow !== 1 ? 'es' : ''} em pausa`}
            icon={Coffee}
            variant={onBreakNow > 0 ? 'warning' : 'neutral'}
            loading={loadingToday}
          />
          <MetricCard
            title="Horas Lançadas Hoje"
            value={formatHours(totalLaunchedToday)}
            subtitle="Em projetos e atividades"
            icon={Briefcase}
            variant={totalLaunchedToday > 0 ? 'positive' : 'neutral'}
            loading={loadingTodayAlloc}
          />
        </div>

        {/* Barra comparativa trabalhado vs esperado hoje */}
        {!isLoading && totalExpectedToday > 0 && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Progresso da equipe hoje</span>
                  <span className="font-mono font-semibold">
                    {formatHours(totalWorkedToday)} <span className="text-muted-foreground font-normal">/ {formatHours(totalExpectedToday)}</span>
                  </span>
                </div>
                <MiniProgressBar
                  value={totalWorkedToday}
                  max={totalExpectedToday}
                  colorClass={
                    totalWorkedToday >= totalExpectedToday - 0.01
                      ? 'bg-green-500'
                      : totalWorkedToday / totalExpectedToday > 0.6
                        ? 'bg-primary'
                        : 'bg-primary/60'
                  }
                />
                <div className="grid grid-cols-3 gap-4 text-center pt-1">
                  <div>
                    <p className="text-xs text-muted-foreground">Online</p>
                    <p className="font-semibold text-sm">{onlineToday}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Em intervalo</p>
                    <p className="font-semibold text-sm text-amber-600 dark:text-amber-400">{onBreakNow}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Ponto aberto</p>
                    <p className={cn('font-semibold text-sm', pendingClockOut > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground')}>{pendingClockOut}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══════════════════════════════════════════════
          SEÇÃO 2: BANCO DE HORAS
      ═══════════════════════════════════════════════ */}
      <div className="space-y-3">
        <SectionTitle icon={TrendingUp} title="Banco de Horas" description="Saldo consolidado da equipe no mês" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard
            title="Saldo Total"
            value={`${totalBalance >= 0 ? '+' : ''}${formatHours(totalBalance)}`}
            subtitle="Soma de todos os colaboradores"
            icon={totalBalance >= 0 ? TrendingUp : TrendingDown}
            variant={totalBalance > 0 ? 'positive' : totalBalance < 0 ? 'negative' : 'neutral'}
            loading={loadingTimesheet}
          />
          <MetricCard
            title="Média por Colaborador"
            value={`${avgBalance >= 0 ? '+' : ''}${formatHours(avgBalance)}`}
            subtitle={`${employeeBalances.length} colaborador${employeeBalances.length !== 1 ? 'es' : ''} com registro`}
            icon={BarChart3}
            variant={avgBalance > 0 ? 'positive' : avgBalance < 0 ? 'negative' : 'neutral'}
            loading={loadingTimesheet}
          />
          <MetricCard
            title="Saldo Positivo"
            value={positiveCount}
            subtitle={`colaborador${positiveCount !== 1 ? 'es' : ''} com saldo a favor`}
            icon={TrendingUp}
            variant="positive"
            loading={loadingTimesheet}
          />
          <MetricCard
            title="Saldo Negativo"
            value={negativeCount}
            subtitle={`colaborador${negativeCount !== 1 ? 'es' : ''} com débito`}
            icon={TrendingDown}
            variant={negativeCount > 0 ? 'negative' : 'neutral'}
            loading={loadingTimesheet}
          />
        </div>

        {/* Ranking: maior positivo e maior negativo */}
        {!loadingTimesheet && employeeBalances.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {maxPositive.balance > 0 && (
              <RankCard
                label="Maior saldo positivo"
                name={maxPositive.name}
                value={`+${formatHours(maxPositive.balance)}`}
                variant="positive"
              />
            )}
            {maxNegative.balance < 0 && (
              <RankCard
                label="Maior saldo negativo"
                name={maxNegative.name}
                value={formatHours(maxNegative.balance)}
                variant="negative"
              />
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════
          SEÇÃO 4: PROJETOS & ALOCAÇÃO
      ═══════════════════════════════════════════════ */}
      <div className="space-y-3">
        <SectionTitle icon={FolderOpen} title="Projetos & Alocação" description="Projetos ativos e horas lançadas no mês" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard
            title="Projetos Ativos"
            value={activeProjects.length}
            subtitle={`${(projects ?? []).length} total cadastrados`}
            icon={FolderOpen}
            variant="default"
            loading={loadingProjects}
          />
          <MetricCard
            title="Total de Atividades"
            value={totalActivities}
            subtitle="em projetos ativos"
            icon={Activity}
            variant="default"
            loading={loadingProjects}
          />
          <MetricCard
            title="Horas Alocadas"
            value={formatHours(totalMonthAllocated)}
            subtitle="lançadas em projetos no mês"
            icon={Briefcase}
            variant={totalMonthAllocated > 0 ? 'positive' : 'neutral'}
            loading={loadingMonthAlloc}
          />
          <MetricCard
            title="Taxa de Alocação"
            value={totalMonthWorked > 0 ? `${allocationRate}%` : '—'}
            subtitle="horas lançadas vs trabalhadas"
            icon={BarChart3}
            variant={allocationRate >= 80 ? 'positive' : allocationRate >= 50 ? 'warning' : totalMonthWorked === 0 ? 'neutral' : 'negative'}
            loading={loadingMonthAlloc || loadingTimesheet}
          />
        </div>

        {/* Barra de alocação do mês */}
        {!loadingMonthAlloc && !loadingTimesheet && totalMonthWorked > 0 && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Horas lançadas em projetos (mês)</span>
                  <span className="font-mono font-semibold">
                    {formatHours(totalMonthAllocated)} <span className="text-muted-foreground font-normal">/ {formatHours(totalMonthWorked)}</span>
                  </span>
                </div>
                <MiniProgressBar
                  value={totalMonthAllocated}
                  max={totalMonthWorked}
                  colorClass={
                    allocationRate >= 80
                      ? 'bg-green-500'
                      : allocationRate >= 40
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                  }
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══════════════════════════════════════════════
          SEÇÃO 5: AJUSTES & APROVAÇÕES
      ═══════════════════════════════════════════════ */}
      <div className="space-y-3">
        <SectionTitle icon={ClipboardList} title="Ajustes & Aprovações" description="Solicitações de ajuste de ponto no mês" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <MetricCard
            title="SLA Atrasado"
            value={overdueCount}
            subtitle={overdueCount === 0 ? 'Nenhum atrasado' : `${overdueCount} fora do prazo`}
            icon={overdueCount === 0 ? CheckCircle : AlertCircle}
            variant={overdueCount === 0 ? 'positive' : 'negative'}
          />
          <MetricCard
            title="Pendentes"
            value={pendingCount}
            subtitle={pendingCount === 0 ? 'Nenhum aguardando' : `aguardando revisão`}
            icon={pendingCount === 0 ? CheckCircle : AlertCircle}
            variant={pendingCount === 0 ? 'positive' : 'warning'}
          />
          <MetricCard
            title="Aprovados no Mês"
            value={approvedCount}
            subtitle="ajustes aprovados"
            icon={CheckCircle}
            variant={approvedCount > 0 ? 'positive' : 'neutral'}
            loading={loadingAdj}
          />
          <MetricCard
            title="Rejeitados no Mês"
            value={rejectedCount}
            subtitle="ajustes reprovados"
            icon={XCircle}
            variant={rejectedCount > 0 ? 'negative' : 'neutral'}
            loading={loadingAdj}
          />
          <MetricCard
            title="Taxa de Aprovação"
            value={totalReviewed > 0 ? `${approvalRate}%` : '—'}
            subtitle={totalReviewed > 0 ? `${totalReviewed} revisado${totalReviewed !== 1 ? 's' : ''}` : 'Nenhum revisado ainda'}
            icon={BarChart3}
            variant={approvalRate >= 80 ? 'positive' : approvalRate >= 50 ? 'warning' : totalReviewed === 0 ? 'neutral' : 'negative'}
            loading={loadingAdj}
          />
        </div>
      </div>
    </div>
  );
}
